/**
 * The agent template's runtime calls from the app (migration 923):
 * metering (§2 K), delivery (§2 F), tasks both ways (§2 D, E), the sponsor
 * (§2 C), reviewer rights (§2 G), sign-offs (§3) and the readiness_failed
 * event (§2 E). Each is one database function; the server runtime calls the
 * same functions with the service role.
 */

import { getSupabaseClient } from './supabaseClient';

export interface AgentAiUse {
  allowed: boolean;
  used: number;
  limit: number;
  error: string | null;
}

/** Count one model call for an agent against its monthly cap. */
export async function countAgentModelCall(workspaceId: string, agentType: string): Promise<AgentAiUse> {
  const { data, error } = await getSupabaseClient().rpc('use_agent_ai', { p_workspace: workspaceId, p_agent_type: agentType });
  if (error) return { allowed: false, used: 0, limit: 0, error: `The model call was not counted: ${error.message}` };
  const d = (data ?? {}) as { allowed?: boolean; used?: number; limit?: number };
  return { allowed: d.allowed === true, used: Number(d.used ?? 0), limit: Number(d.limit ?? 0), error: null };
}

export async function getAgentAiUsage(workspaceId: string, agentType: string): Promise<{ used: number; limit: number; feature: string } | null> {
  const { data, error } = await getSupabaseClient().rpc('get_agent_ai_usage', { p_workspace: workspaceId, p_agent_type: agentType });
  if (error || !data) return null;
  const d = data as { used?: number; limit?: number; feature?: string };
  return { used: Number(d.used ?? 0), limit: Number(d.limit ?? 0), feature: String(d.feature ?? '') };
}

/** Queue what an agent produced for people; the ambient agent_run job delivers it by the agent's digest. */
export async function enqueueAgentDelivery(p: {
  workspaceId: string;
  agentType: string;
  title: string;
  body?: string;
  link?: string | null;
  itemIds?: string[];
  runId?: string | null;
  severity?: 'info' | 'low' | 'medium' | 'high' | 'critical' | null;
  confidence?: number | null;
}): Promise<void> {
  const { error } = await getSupabaseClient().rpc('enqueue_agent_delivery', {
    p_workspace_id: p.workspaceId,
    p_agent_type: p.agentType,
    p_title: p.title,
    p_body: p.body ?? '',
    p_link: p.link ?? null,
    p_item_ids: p.itemIds ?? [],
    p_run_id: p.runId ?? null,
    p_severity: p.severity ?? null,
    p_confidence: p.confidence ?? null,
  });
  // A delivery never costs the output it announces.
  if (error) console.warn('[agentRuntime] delivery not queued:', error.message);
}

/** An agent asks a person (a clarify task). Returns the task id, or null when nobody can be asked. */
export async function createAgentTask(p: {
  workspaceId: string;
  agentType: string;
  kind: 'clarify' | 'record_actual';
  itemIds: string[];
  assigneeId: string;
  name: string;
  description?: string;
  attributeKey?: string | null;
}): Promise<string | null> {
  const { data, error } = await getSupabaseClient().rpc('create_agent_task', {
    p_workspace_id: p.workspaceId,
    p_agent_type: p.agentType,
    p_kind: p.kind,
    p_item_ids: p.itemIds,
    p_assignee: p.assigneeId,
    p_name: p.name,
    p_description: p.description ?? null,
    p_attribute_key: p.attributeKey ?? null,
  });
  if (error) {
    console.warn('[agentRuntime] task not created:', error.message);
    return null;
  }
  return (data as string | null) ?? null;
}

/** A person gives a task to an agent. */
export async function assignTaskToAgent(p: {
  workspaceId: string;
  agentType: string;
  itemIds: string[];
  name: string;
  attributeKey?: string | null;
}): Promise<{ taskId: string | null; error: string | null }> {
  const { data, error } = await getSupabaseClient().rpc('assign_task_to_agent', {
    p_workspace_id: p.workspaceId,
    p_agent_type: p.agentType,
    p_item_ids: p.itemIds,
    p_name: p.name,
    p_attribute_key: p.attributeKey ?? null,
  });
  if (error) return { taskId: null, error: error.message };
  return { taskId: (data as string | null) ?? null, error: null };
}

export interface AgentSponsorStatus {
  sponsorUserId: string | null;
  sponsorName: string | null;
  sponsorIsAdmin: boolean;
}

export async function getAgentSponsorStatus(workspaceId: string, agentType: string): Promise<AgentSponsorStatus | null> {
  const { data, error } = await getSupabaseClient().rpc('get_agent_sponsor_status', { p_workspace_id: workspaceId, p_agent_type: agentType });
  if (error || !data) return null;
  const d = data as { sponsor_user_id?: string | null; sponsor_name?: string | null; sponsor_is_admin?: boolean };
  return { sponsorUserId: d.sponsor_user_id ?? null, sponsorName: d.sponsor_name ?? null, sponsorIsAdmin: d.sponsor_is_admin === true };
}

/** May this person accept or reject this agent's output here? The database enforces the same rule. */
export async function agentReviewerAllowed(workspaceId: string, agentType: string, userId: string): Promise<boolean> {
  const { data, error } = await getSupabaseClient().rpc('agent_reviewer_allowed', {
    p_workspace_id: workspaceId,
    p_agent_type: agentType,
    p_user: userId,
  });
  if (error) return false;
  return data === true;
}

/** itemId → { agentType: true } for every agent that signed off on the item. */
export async function loadAgentSignoffs(workspaceId: string, itemIds: string[]): Promise<Record<string, Record<string, boolean>>> {
  if (!itemIds.length) return {};
  const out: Record<string, Record<string, boolean>> = {};
  for (let i = 0; i < itemIds.length; i += 300) {
    const { data } = await getSupabaseClient()
      .from('agent_signoffs')
      .select('item_id, agent_type')
      .eq('workspace_id', workspaceId)
      .in('item_id', itemIds.slice(i, i + 300));
    for (const row of (data ?? []) as Array<{ item_id: string; agent_type: string }>) {
      (out[row.item_id] ??= {})[row.agent_type] = true;
    }
  }
  return out;
}

/** Readiness held items back: record it once a day so agents watching readiness_failed wake. */
export async function recordReadinessFailed(workspaceId: string, itemIds: string[], codes: string[]): Promise<void> {
  if (!itemIds.length) return;
  const { error } = await getSupabaseClient().rpc('record_readiness_failed', {
    p_workspace_id: workspaceId,
    p_item_ids: itemIds,
    p_codes: codes,
  });
  if (error) console.warn('[agentRuntime] readiness event not recorded:', error.message);
}
